<script setup>
import {useFetch} from "@/plugins/fetch";
import {onMounted, ref} from "vue";
import {useToast} from "primevue/usetoast";
import UAParser from "ua-parser-js";

const fetch = useFetch();
const toast = useToast();
const parser = new UAParser();

const loginAttempts = ref([]);
const isLoading = ref(true);

onMounted(async () => {
  await fetchAttempts();
});

const fetchAttempts = async () => {
  isLoading.value = true;
  try {
    const response = await fetch('user/security/login-attempts/');
    const attempts = response?.data || response || [];
    loginAttempts.value = (Array.isArray(attempts) ? attempts : []).map(attempt => ({
      ...attempt,
      parsedUA: parser.setUA(attempt.user_agent || '').getResult(),
    }));
  } catch (error) {
    console.error('Error fetching login attempts:', error);
  } finally {
    isLoading.value = false;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString.date || dateString);
  return date.toLocaleString('en-GB', {dateStyle: 'medium', timeStyle: 'short'});
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString.date || dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
};

const getStatusInfo = (attempt) => {
  if (attempt.revoked) {
    return { icon: 'fa-ban', class: 'revoked', label: 'Revoked' };
  }
  if (attempt.successful) {
    return { icon: 'fa-check-circle', class: 'success', label: 'Success' };
  }
  return { icon: 'fa-exclamation-circle', class: 'failed', label: 'Failed' };
};

const getBrowserIcon = (browserName) => {
  const name = (browserName || '').toLowerCase();
  if (name.includes('chrome')) return 'fa-brands fa-chrome';
  if (name.includes('firefox')) return 'fa-brands fa-firefox-browser';
  if (name.includes('safari')) return 'fa-brands fa-safari';
  if (name.includes('edge')) return 'fa-brands fa-edge';
  if (name.includes('opera')) return 'fa-brands fa-opera';
  return 'fa-globe';
};

const getOSIcon = (osName) => {
  const name = (osName || '').toLowerCase();
  if (name.includes('windows')) return 'fa-brands fa-windows';
  if (name.includes('mac') || name.includes('ios')) return 'fa-brands fa-apple';
  if (name.includes('android')) return 'fa-brands fa-android';
  if (name.includes('linux')) return 'fa-brands fa-linux';
  return 'fa-desktop';
};

const revokeToken = async (attemptId) => {
  try {
    const response = await fetch('user/security/revoke/', { method: 'POST', body: {id: attemptId} });
    await fetchAttempts();
    toast.add({severity: 'success', summary: 'Success', detail: response?.message || 'Access revoked', life: 3000});
  } catch (error) {
    toast.add({severity: 'error', summary: 'Error', detail: 'Failed to revoke access', life: 3000});
  }
};
</script>

<template>
  <div class="login-attempts-card">
    <div class="card-header">
      <div class="header-icon">
        <i class="fas fa-history"></i>
      </div>
      <div class="header-text">
        <h3>Recent Logins</h3>
        <p>Monitor your account activity</p>
      </div>
    </div>

    <div class="card-body">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <i class="fas fa-circle-notch fa-spin"></i>
        <span>Loading activity...</span>
      </div>

      <!-- Empty State -->
      <div v-else-if="!loginAttempts.length" class="empty-state">
        <i class="fas fa-shield-alt"></i>
        <p>No login activity recorded yet</p>
      </div>

      <!-- Attempts List -->
      <div v-else class="attempts-list">
        <div
          v-for="attempt in loginAttempts.slice(0, 5)"
          :key="attempt.id"
          class="attempt-item"
          :class="getStatusInfo(attempt).class"
        >
          <div class="attempt-status">
            <i :class="['fas', getStatusInfo(attempt).icon]"></i>
          </div>
          
          <div class="attempt-info">
            <div class="attempt-main">
              <span class="browser-info">
                <i :class="getBrowserIcon(attempt.parsedUA?.browser?.name)"></i>
                {{ attempt.parsedUA?.browser?.name || 'Unknown Browser' }}
              </span>
              <span class="os-info">
                <i :class="getOSIcon(attempt.parsedUA?.os?.name)"></i>
                {{ attempt.parsedUA?.os?.name || 'Unknown OS' }}
              </span>
            </div>
            
            <div class="attempt-details">
              <span class="location" v-if="attempt.city || attempt.region">
                <i class="fas fa-map-marker-alt"></i>
                {{ [attempt.city, attempt.region].filter(Boolean).join(', ') }}
              </span>
              <span class="ip-address" v-if="attempt.ip_address">
                <i class="fas fa-network-wired"></i>
                {{ attempt.ip_address }}
              </span>
              <span class="timestamp">
                <i class="fas fa-clock"></i>
                {{ formatRelativeTime(attempt.timestamp) }}
              </span>
            </div>

            <div class="attempt-tags">
              <span v-if="attempt.revoked" class="tag revoked">
                <i class="fas fa-ban"></i> Revoked
              </span>
              <span v-else-if="!attempt.successful" class="tag failed">
                <i class="fas fa-times"></i> Failed
              </span>
              <span v-else-if="attempt.is_current" class="tag current">
                <i class="fas fa-check"></i> Current Session
              </span>
            </div>
          </div>

          <div class="attempt-actions">
            <button
              v-if="attempt.successful && !attempt.revoked && !attempt.is_current"
              class="revoke-btn"
              @click="revokeToken(attempt.id)"
              title="Revoke this session"
            >
              <i class="fas fa-user-slash"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- View All Link -->
      <div v-if="loginAttempts.length > 5" class="view-all">
        <button class="view-all-btn">
          <i class="fas fa-list"></i>
          View All Activity ({{ loginAttempts.length }} total)
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login-attempts-card {
  background: var(--calid-card-bg, rgba(17, 24, 39, 0.8));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 16px;
  overflow: hidden;
  height: 100%;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-bottom: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  background: var(--calid-card-bg, rgba(17, 24, 39, 0.8));
}

.header-icon {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon i {
  font-size: 1.125rem;
  color: white;
}

.header-text h3 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0 0 0.125rem;
}

.header-text p {
  font-size: 0.85rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
}

.card-body {
  padding: 1rem;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 0.75rem;
  color: var(--calid-text-secondary, #94a3b8);
}

.loading-state i,
.empty-state i {
  font-size: 1.5rem;
  opacity: 0.6;
}

.attempts-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.attempt-item {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.06));
  border-radius: 12px;
  transition: all 0.2s ease;
}

.attempt-item:hover {
  border-color: rgba(255, 255, 255, 0.1);
}

.attempt-item.revoked {
  opacity: 0.6;
}

.attempt-status {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.attempt-item.success .attempt-status {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.attempt-item.failed .attempt-status {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

.attempt-item.revoked .attempt-status {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.attempt-info {
  flex: 1;
  min-width: 0;
}

.attempt-main {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
}

.browser-info,
.os-info {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--calid-text-primary, #f0f0f5);
}

.browser-info i,
.os-info i {
  font-size: 0.85rem;
  opacity: 0.7;
}

.attempt-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: var(--calid-text-secondary, #94a3b8);
}

.location,
.ip-address,
.timestamp {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.location i,
.ip-address i,
.timestamp i {
  font-size: 0.7rem;
  opacity: 0.7;
}

.attempt-tags {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 500;
}

.tag.revoked {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.tag.failed {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

.tag.current {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.attempt-actions {
  flex-shrink: 0;
}

.revoke-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.revoke-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: scale(1.05);
}

.view-all {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
}

.view-all-btn {
  width: 100%;
  padding: 0.75rem;
  background: transparent;
  border: 1px dashed var(--calid-border, rgba(255, 255, 255, 0.15));
  border-radius: 10px;
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.view-all-btn:hover {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(0, 212, 255, 0.3);
  color: #00d4ff;
}

/* Light mode */
:root:not(.dark-version) .login-attempts-card {
  background: white;
  border-color: #e5e7eb;
}

:root:not(.dark-version) .card-header {
  border-bottom-color: #f3f4f6;
}

:root:not(.dark-version) .header-text h3 {
  color: #1f2937;
}

:root:not(.dark-version) .header-text p {
  color: #6b7280;
}

:root:not(.dark-version) .attempt-item {
  background: #f9fafb;
  border-color: #e5e7eb;
}

:root:not(.dark-version) .browser-info,
:root:not(.dark-version) .os-info {
  color: #1f2937;
}

:root:not(.dark-version) .attempt-details {
  color: #6b7280;
}

:root:not(.dark-version) .view-all {
  border-top-color: #f3f4f6;
}

:root:not(.dark-version) .view-all-btn {
  border-color: #e5e7eb;
  color: #6b7280;
}
</style>
